import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { useAuthStore } from './stores/auth-store'
import { useOrgStore } from './stores/org-store'
import * as authApi from './services/auth-api'
import Layout from './components/Layout'
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import DashboardPage from './pages/DashboardPage'
import CompaniesPage from './pages/CompaniesPage'
import CompanyDetailPage from './pages/CompanyDetailPage'
import CompanyOutreachPage from './pages/CompanyOutreachPage'
import SourcesPage from './pages/SourcesPage'
import SourceConfigPage from './pages/SourceConfigPage'
import ListsPage from './pages/ListsPage'
import RecommendedProspectsPage from './pages/RecommendedProspectsPage'
import JobsPage from './pages/JobsPage'
import OutreachPage from './pages/OutreachPage'
import OutreachTemplatesPage from './pages/OutreachTemplatesPage'
import OutreachTemplateDetailPage from './pages/OutreachTemplateDetailPage'
import CampaignsPage from './pages/CampaignsPage'
import CampaignDetailPage from './pages/CampaignDetailPage'
import DealsPage from './pages/DealsPage'
import DealDetailPage from './pages/DealDetailPage'
import OnboardingLayout from './pages/onboarding/OnboardingLayout'
import ProfileStep from './pages/onboarding/ProfileStep'
import OrgChoiceStep from './pages/onboarding/OrgChoiceStep'
import CreateOrgStep from './pages/onboarding/CreateOrgStep'
import InviteTokenStep from './pages/onboarding/InviteTokenStep'
import BrowseOrgsStep from './pages/onboarding/BrowseOrgsStep'
import PendingApprovalPage from './pages/onboarding/PendingApprovalPage'
import OrgSettingsPage from './pages/settings/OrgSettingsPage'
import AccountSettingsPage from './pages/settings/AccountSettingsPage'
import TeamPage from './pages/settings/TeamPage'
import DemoAnalysisPage from './pages/public/DemoAnalysisPage'
import AnalysisResultsPage from './pages/public/AnalysisResultsPage'
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'

const DIRECT_ACCESS_TOKEN_COOKIE_NAMES = ['access_token', 'sb-access-token']
const DIRECT_REFRESH_TOKEN_COOKIE_NAMES = ['refresh_token', 'sb-refresh-token']

interface SessionTokens {
  accessToken: string | null
  refreshToken: string | null
}

let rootSessionRefreshPromise: Promise<authApi.AuthResponse> | null = null
let rootSessionRefreshToken: string | null = null

function refreshSessionFromToken(refreshToken: string): Promise<authApi.AuthResponse> {
  if (!rootSessionRefreshPromise || rootSessionRefreshToken !== refreshToken) {
    rootSessionRefreshToken = refreshToken
    rootSessionRefreshPromise = authApi.refreshToken(refreshToken).finally(() => {
      rootSessionRefreshPromise = null
      rootSessionRefreshToken = null
    })
  }
  return rootSessionRefreshPromise
}

function pickFirstString(record: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.length > 0) {
      return value
    }
  }
  return null
}

function decodeBase64Url(value: string): string | null {
  try {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4)
    return atob(padded)
  } catch {
    return null
  }
}

function parseSupabaseAuthCookieValue(rawValue: string): SessionTokens {
  const decodedValue = rawValue.startsWith('base64-')
    ? decodeBase64Url(rawValue.slice('base64-'.length))
    : rawValue

  if (!decodedValue) {
    return { accessToken: null, refreshToken: null }
  }

  try {
    const parsed = JSON.parse(decodedValue) as unknown

    if (Array.isArray(parsed)) {
      return {
        accessToken: typeof parsed[0] === 'string' ? parsed[0] : null,
        refreshToken: typeof parsed[1] === 'string' ? parsed[1] : null
      }
    }

    if (parsed && typeof parsed === 'object') {
      const record = parsed as Record<string, unknown>
      return {
        accessToken: pickFirstString(record, ['access_token', 'accessToken']),
        refreshToken: pickFirstString(record, ['refresh_token', 'refreshToken'])
      }
    }
  } catch {
    return { accessToken: null, refreshToken: null }
  }

  return { accessToken: null, refreshToken: null }
}

function readSessionTokensFromCookies(): SessionTokens {
  if (typeof document === 'undefined') {
    return { accessToken: null, refreshToken: null }
  }

  const cookieEntries = document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .filter(Boolean)

  const cookieMap = new Map<string, string>()

  for (const cookie of cookieEntries) {
    const separatorIndex = cookie.indexOf('=')
    if (separatorIndex <= 0) continue
    const key = cookie.slice(0, separatorIndex).trim()
    const value = decodeURIComponent(cookie.slice(separatorIndex + 1).trim())
    cookieMap.set(key, value)
  }

  const directAccessToken = DIRECT_ACCESS_TOKEN_COOKIE_NAMES
    .map((name) => cookieMap.get(name))
    .find((value): value is string => Boolean(value))
  const directRefreshToken = DIRECT_REFRESH_TOKEN_COOKIE_NAMES
    .map((name) => cookieMap.get(name))
    .find((value): value is string => Boolean(value))

  if (directAccessToken || directRefreshToken) {
    return {
      accessToken: directAccessToken ?? null,
      refreshToken: directRefreshToken ?? null
    }
  }

  for (const [name, value] of cookieMap.entries()) {
    if (!name.startsWith('sb-') || !name.endsWith('-auth-token')) {
      continue
    }

    const parsed = parseSupabaseAuthCookieValue(value)
    if (parsed.accessToken || parsed.refreshToken) {
      return parsed
    }
  }

  return { accessToken: null, refreshToken: null }
}

/**
 * Syncs onboarding status from the backend on app startup.
 * This prevents already-onboarded users from seeing the flow again
 * after localStorage is cleared, browser changes, etc.
 */
function useOnboardingSync() {
  const { isAuthenticated, onboardingSynced, syncOnboardingStatus } = useAuthStore()
  const { orgsSynced } = useOrgStore()

  useEffect(() => {
    if (isAuthenticated && (!onboardingSynced || !orgsSynced)) {
      syncOnboardingStatus()
    }
  }, [isAuthenticated, onboardingSynced, orgsSynced, syncOnboardingStatus])

  return { isSyncing: isAuthenticated && (!onboardingSynced || !orgsSynced) }
}

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  )
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore()

  if (isLoading) {
    return <FullPageSpinner />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function OnboardedRoute({ children }: { children: React.ReactNode }) {
  const { onboardingCompleted, onboardingSynced } = useAuthStore()
  const { currentOrg, orgsSynced, isLoading: orgsLoading } = useOrgStore()

  // Wait until the onboarding status has been synced with the backend
  // (syncOnboardingStatus now populates both auth and org stores in a single call)
  if (!onboardingSynced) {
    return <FullPageSpinner />
  }

  if (!onboardingCompleted) {
    return <Navigate to="/onboarding/profile" replace />
  }

  // Wait for orgs to be synced before checking currentOrg
  // orgsSynced is set by syncOnboardingStatus when it populates the org store
  if (!orgsSynced || orgsLoading) {
    return <FullPageSpinner />
  }

  if (!currentOrg) {
    return <Navigate to="/onboarding/pending" replace />
  }

  return <>{children}</>
}

function OnboardingRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, onboardingCompleted, onboardingSynced } = useAuthStore()
  if (!isAuthenticated) return <Navigate to="/login" replace />

  // Wait until the onboarding status has been synced with the backend
  if (!onboardingSynced) return <FullPageSpinner />

  if (onboardingCompleted) return <Navigate to="/app/dashboard" replace />
  return <>{children}</>
}

function LandingRoute() {
  const [isCheckingSession, setIsCheckingSession] = useState(false)
  const [shouldRedirect, setShouldRedirect] = useState(false)

  useEffect(() => {
    let cancelled = false

    const checkSession = async () => {
      const authState = useAuthStore.getState()
      const cookieTokens = readSessionTokensFromCookies()
      const refreshToken = authState.tokens?.refresh_token || cookieTokens.refreshToken

      if (!refreshToken) {
        const expiresAtMs = (authState.tokens?.expires_at || 0) * 1000
        const hasValidAccessToken =
          Boolean(authState.tokens?.access_token) && expiresAtMs > Date.now()

        if (!cancelled) {
          setShouldRedirect(authState.isAuthenticated && hasValidAccessToken)
          setIsCheckingSession(false)
        }
        return
      }

      if (!cancelled) {
        setIsCheckingSession(true)
      }

      try {
        const session = await refreshSessionFromToken(refreshToken)
        if (cancelled) return

        useAuthStore.setState({
          user: session.user,
          tokens: {
            access_token: session.access_token,
            refresh_token: session.refresh_token,
            expires_at: session.expires_at
          },
          isAuthenticated: true,
          onboardingCompleted: !!session.user.onboarding_completed,
          onboardingSynced: false,
          isLoading: false,
          error: null
        })

        setShouldRedirect(true)
      } catch {
        if (cancelled) return
        // Keep root route safe by clearing stale local auth state.
        const state = useAuthStore.getState()
        if (state.isAuthenticated || state.tokens) {
          await state.signOut()
        }
        if (!cancelled) {
          setShouldRedirect(false)
        }
      } finally {
        if (!cancelled) {
          setIsCheckingSession(false)
        }
      }
    }

    checkSession()

    return () => {
      cancelled = true
    }
  }, [])

  if (isCheckingSession) {
    return <FullPageSpinner />
  }

  if (shouldRedirect) {
    return <Navigate to="/dashboard" replace />
  }

  return <LandingPage />
}

function LegacyCompanyRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={id ? `/app/companies/${id}` : '/app/companies'} replace />
}

function LegacyCompanyOutreachRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={id ? `/app/companies/${id}/outreach` : '/app/companies'} replace />
}

export default function App() {
  // Trigger the onboarding sync on app startup
  useOnboardingSync()

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes - no authentication required */}
        <Route path="/" element={<LandingRoute />} />
        <Route path="/demo" element={<DemoAnalysisPage />} />
        <Route path="/analyze/:id" element={<AnalysisResultsPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* Pending approval page - not behind OnboardingRoute since user has completed profile */}
        <Route
          path="/onboarding/pending"
          element={
            <PrivateRoute>
              <PendingApprovalPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/onboarding"
          element={
            <OnboardingRoute>
              <OnboardingLayout />
            </OnboardingRoute>
          }
        >
          <Route index element={<Navigate to="/onboarding/profile" replace />} />
          <Route path="profile" element={<ProfileStep />} />
          <Route path="organization" element={<OrgChoiceStep />} />
          <Route path="create-organization" element={<CreateOrgStep />} />
          <Route path="invite-token" element={<InviteTokenStep />} />
          <Route path="browse-organizations" element={<BrowseOrgsStep />} />
        </Route>
        <Route
          path="/app"
          element={
            <PrivateRoute>
              <OnboardedRoute>
                <Layout />
              </OnboardedRoute>
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/app/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="companies" element={<CompaniesPage />} />
          <Route path="companies/:id" element={<CompanyDetailPage />} />
          <Route path="companies/:id/outreach" element={<CompanyOutreachPage />} />
          <Route path="sources" element={<SourcesPage />} />
          <Route path="sources/:id/config" element={<SourceConfigPage />} />
          <Route path="lists" element={<ListsPage />} />
          <Route path="lists/:listId" element={<ListsPage />} />
          <Route path="prospects/recommended" element={<RecommendedProspectsPage />} />
          <Route path="outreach" element={<OutreachPage />} />
          <Route path="outreach/templates" element={<OutreachTemplatesPage />} />
          <Route path="outreach/templates/:id" element={<OutreachTemplateDetailPage />} />
          <Route path="campaigns" element={<CampaignsPage />} />
          <Route path="campaigns/:id" element={<CampaignDetailPage />} />
          <Route path="deals" element={<DealsPage />} />
          <Route path="deals/:id" element={<DealDetailPage />} />
          <Route path="jobs" element={<JobsPage />} />
          <Route path="settings/organization" element={<OrgSettingsPage />} />
          <Route path="settings/account" element={<AccountSettingsPage />} />
          <Route path="settings/team" element={<TeamPage />} />
          <Route path="settings/search-terms" element={<Navigate to="/app/sources" replace />} />
          <Route path="settings/members" element={<Navigate to="/app/settings/team" replace />} />
          <Route path="settings/invites" element={<Navigate to="/app/settings/team" replace />} />
          <Route path="settings/join-requests" element={<Navigate to="/app/settings/team" replace />} />
        </Route>
        {/* Legacy route redirects - keep old paths working */}
        <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
        <Route path="/companies" element={<Navigate to="/app/companies" replace />} />
        <Route
          path="/companies/:id"
          element={<LegacyCompanyRedirect />}
        />
        <Route
          path="/companies/:id/outreach"
          element={<LegacyCompanyOutreachRedirect />}
        />
        <Route path="/sources" element={<Navigate to="/app/sources" replace />} />
        <Route path="/lists" element={<Navigate to="/app/lists" replace />} />
        <Route path="/prospects/recommended" element={<Navigate to="/app/prospects/recommended" replace />} />
        <Route path="/outreach" element={<Navigate to="/app/outreach" replace />} />
        <Route path="/jobs" element={<Navigate to="/app/jobs" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
